import { EmbeddableWorkbookDataQualityWarning } from '@tableau/data-catalog-client-components';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { catalogApolloClient } from '../../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../../catalog/CatalogFeatureFlagsAdapter';
import { ISanitizer } from '../../../commonInfra/utils/Sanitize';
import { DraftWorkbookSpaceProps } from '../../../drafts/types/DraftsTypes';
import { ISpaceItemDetailInfo } from '../../../ts/models/ISpaceItemDetailInfo';
import { IWorkbookActions } from '../../../ts/ServerApi/types/IWorkbookActions';
import { CatalogApiRequests } from '../../../catalog/CatalogApiRequests';
import { DataCatalogUIActionsService } from '../../../ts/services/DataCatalogUIActionsService';
import { SiteUrlsProps } from '../../selectors/SiteUrlsSelectors';
import { EditWorkbookAction } from '../ActionsMenu/EntityActions/EditWorkbookAction';
import { ConnectedExtractMetadata } from '../Header/ExtractMetadata';
import { ConnectedExtractTasksMetadata } from '../Header/ExtractTasksMetadata';
import { ItemDescription } from '../Header/HeaderItemDescription';
import { ModifiedDateMetadata, OwnerMetadata } from '../Header/HeaderMetadataRow';
import { ActionsMenuIcon, DetailButtonIcon, FavoriteToggleWithCountsIcon } from '../Header/HeaderNameRowIcons';
import { ConnectedSpaceHeader } from '../Header/SpaceHeader';
import { SpaceHeaderStyles } from '../Header/SpaceHeaderStyles';
import { WorkbookSpaceHeaderDraftBanner } from '../Header/WorkbookSpaceHeaderDraftBanner';

export interface WorkbookSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'workbook'>;
  readonly availableActions: IWorkbookActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly sanitizer: ISanitizer;
  readonly catalogingOnSiteEnabled: boolean;
  readonly draft?: DraftWorkbookSpaceProps;
}

export function WorkbookSpaceHeader(props: WorkbookSpaceHeaderProps): JSX.Element {
  return <ConnectedSpaceHeader
      item={props.item}
      availableActions={props.availableActions}
      nameRowIcons={
        <React.Fragment>
          { FavoriteToggleWithCountsIcon({
            item: props.item,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          }) }
          { DetailButtonIcon(props.item, props.openDetail) }
          { ActionsMenuIcon(props.item) }
        </React.Fragment>
      }
      metadataRow={
        <React.Fragment>
          { OwnerMetadata(props.item.owner, props.urls) }
          { ModifiedDateMetadata(props.item.updatedAt) }
          <ConnectedExtractMetadata itemInfo={props.item} availableActions={props.availableActions} />
          <ConnectedExtractTasksMetadata itemInfo={props.item} availableActions={props.availableActions} />
          { props.catalogingOnSiteEnabled ?
            DataQualityMetadata(props.item, props.urls) : null }
        </React.Fragment>
      }
      descriptionElement={ ItemDescription(props.item, props.openDetail, props.sanitizer) }
      spotlightActions={props.draft ? [] : [ EditWorkbookAction ]}
      bottomElement={props.draft &&
      <WorkbookSpaceHeaderDraftBanner
            workbookContent={props.item}
            draftInfo={props.draft}
      />}
  />;
}

function DataQualityMetadata(
  workbookInfo: ISpaceItemDetailInfo<'workbook'>,
  urls: SiteUrls,
): JSX.Element | null {
  return (
    <>
      <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
        <EmbeddableWorkbookDataQualityWarning
          client={catalogApolloClient}
          vizportalUrlId={workbookInfo.id}
          siteUrls={urls}
          apiRequests={CatalogApiRequests}
          uiActions={DataCatalogUIActionsService.instance()}
          featureFlags={catalogFeatureFlagsAdapter()}
        />
      </span>
    </>
  );
}
